import { useCallback, useEffect, useMemo } from "react";

import { useGetCountries } from "@/api/country/useCountryAPI";
import { CircleInformation, MapIcon, POIIcon } from "@/assets/icons";
import Dropdown from "@/components/ui/dropdown";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import { DATA_SETTINGS_MESSAGE } from "@/constants/message";
import { DataSettings, EDataSettingSpatialType } from "@/types/dataSetting";

import AggregationBadge, {
  AggregationBadgeValue
} from "../../AggregationBadge";
import SettingCard from "./SettingCard";

const TEST_ID_PREFIX = "dataset-management-data-settings-hub";

type DataSettingsHubProps = {
  countryId?: string;
  dataSetting: DataSettings | null;
  /** Name of the saved geometry column (`is_reference_geo`), if any. */
  spatialAttributeLabel?: string;
  admAreaAttribute?: string;
  /** True when the dataset's schema already carries mapped ADM Area columns. */
  hasSavedAdmAreaMapping: boolean;
  isSavingCountry: boolean;
  setDataSetting: React.Dispatch<React.SetStateAction<DataSettings | null>>;
  /** The user picked a country, which is saved right away. */
  onSelectCountry: (country: IOptionItem) => void;
  onOpenSpatialSettings: () => void;
  onOpenAdmAreaMapping: () => void;
};

const DataSettingsHub = ({
  countryId,
  dataSetting,
  spatialAttributeLabel,
  admAreaAttribute,
  hasSavedAdmAreaMapping,
  isSavingCountry,
  setDataSetting,
  onSelectCountry,
  onOpenSpatialSettings,
  onOpenAdmAreaMapping
}: DataSettingsHubProps) => {
  const { data: countries, isLoading: isGettingCountries } = useGetCountries();

  const handleSelectCountry = useCallback(
    (country: IOptionItem) => {
      setDataSetting((prevDataSetting: DataSettings | null) => ({
        ...prevDataSetting,
        country
      }));
    },
    [setDataSetting]
  );

  const countriesMapping: IOptionItem[] = useMemo(
    () =>
      countries?.map((item) => ({
        label: item.name,
        value: item.id
      })) ?? [],
    [countries]
  );

  // To set default country value by dataset meta data
  useEffect(() => {
    if (!countryId || !countriesMapping?.length) return;
    const country = countriesMapping.find((item) => item.value === countryId);
    if (country) handleSelectCountry(country);
  }, [countryId, countriesMapping, handleSelectCountry]);

  const aggregationType = dataSetting?.spatialAggregation?.type;

  // Only count a country the dataset already has, not one still saving.
  const isCountrySaved = Boolean(countryId) && !isSavingCountry;
  const isSpatialConfigured = Boolean(aggregationType?.value);
  // The endpoint rejects a dataset whose geometry already is an ADM Area: its
  // rows are mapped the moment the geometry is set, so there is nothing to map.
  const isGeomAlreadyAdmArea =
    aggregationType?.value === EDataSettingSpatialType.Admin_Area;
  const isAdmAreaMapped =
    hasSavedAdmAreaMapping || Boolean(admAreaAttribute) || isGeomAlreadyAdmArea;

  return (
    <div className="flex flex-col gap-4">
      <Dropdown
        isLoading={isGettingCountries}
        placeholder="Country*"
        data={countriesMapping}
        value={dataSetting?.country || null}
        disabled={isSavingCountry}
        onSelect={(data) => {
          if (!data || data.value === dataSetting?.country?.value) return;
          onSelectCountry(data);
        }}
      />

      <SettingCard
        icon={<POIIcon className="w-6 h-6 fill-primary-teal" />}
        title={DATA_SETTINGS_MESSAGE.SPATIAL_TITLE}
        badge={
          <AggregationBadge
            value={aggregationType?.value as AggregationBadgeValue}
          />
        }
        description={DATA_SETTINGS_MESSAGE.SPATIAL_CARD_DESCRIPTION}
        buttonLabel={isSpatialConfigured ? "Edit Config" : "Start Config"}
        // Country scopes the areas the geometry is matched against, so it has to
        // be picked before the geometry is.
        isButtonDisabled={!isCountrySaved}
        testId={`${TEST_ID_PREFIX}-spatial`}
        onButtonClick={onOpenSpatialSettings}
      >
        {isSpatialConfigured && (
          <span className="text-xs text-txtBody">
            Aggregation: <b>{aggregationType?.label}</b>
            {spatialAttributeLabel && (
              <>
                {" · "}Spatial attribute: <b>{spatialAttributeLabel}</b>
              </>
            )}
          </span>
        )}
      </SettingCard>

      <SettingCard
        icon={<MapIcon className="w-6 h-6 fill-primary-teal" />}
        title={DATA_SETTINGS_MESSAGE.ADM_AREA_TITLE}
        titleSuffix={DATA_SETTINGS_MESSAGE.ADM_AREA_TITLE_SUFFIX}
        badge={
          <AggregationBadge
            value={isAdmAreaMapped ? "adm_area_mapping" : undefined}
          />
        }
        // Once the geometry is an ADM Area the card explains that instead; the
        // invitation to map attributes no longer applies.
        description={
          isGeomAlreadyAdmArea
            ? undefined
            : DATA_SETTINGS_MESSAGE.ADM_AREA_CARD_DESCRIPTION
        }
        buttonLabel={isAdmAreaMapped ? "Edit Config" : "Start Config"}
        isButtonDisabled={
          !isCountrySaved || !isSpatialConfigured || isGeomAlreadyAdmArea
        }
        testId={`${TEST_ID_PREFIX}-adm-area`}
        onButtonClick={onOpenAdmAreaMapping}
      >
        {isGeomAlreadyAdmArea && (
          <span className="text-xs text-dim-gray">
            {DATA_SETTINGS_MESSAGE.ADM_AREA_ALREADY_MAPPED_NOTE}
          </span>
        )}
        {/* Design keeps this note visible even once the button unlocks; it only
            goes away after the mapping itself is configured. */}
        {!isAdmAreaMapped && (
          <span className="flex items-center gap-1 text-xs text-dim-gray">
            <CircleInformation className="w-3.5 h-3.5 fill-dim-gray" />
            {DATA_SETTINGS_MESSAGE.ADM_AREA_REQUIRES_SPATIAL_NOTE}
          </span>
        )}
      </SettingCard>
    </div>
  );
};

export default DataSettingsHub;
